import React, { useCallback, useState } from 'react';
import {
  getFormattedTime,
  isMiniscreen,
  MiniscreenMode,
  openWithMiniscreen,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';

import styles from './NightViewPanel.module.scss';
import { IconButton } from '../../common/ui-kit/IconButton';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { MiniscreenModeIcon } from '../../common/icons/miniscreen/MiniscreenModeIcon';
import clsx from 'clsx';
import { UniLink } from '../../common/ui-kit/buttons/UniLink';
import { ResourceDto } from '@lmt/shared-types';

export interface MeasurementInfo {
  id: number;
  name: string;
}

export interface NightViewInfo {
  id: number;
  room: ResourceDto;
  startDate: Date;
  endDate: Date;
  patientId: number;
  patientName: string;
  subtenantCode: string;
  caseId: number;
  visitType: string;
  measurements: MeasurementInfo[];
}

export interface Props {
  currentRoom: number | null;
  visit: NightViewInfo;
  isStartDateHidden: boolean;
  hasPlaceColumn: boolean;
  openPatientTab: (patientId: number) => void;
  openCaseVisitTab: (
    patientId: number,
    caseId: number,
    visitId: number,
  ) => void;
  openMeasurementTab: (
    patientId: number,
    caseId: number,
    visitId: number,
    measurementId: number,
  ) => void;
  startMiniscreenOnline: boolean;
  startMiniscreenOffline: boolean;
  isOnlineMeasurementsDisabled: boolean;
}

export const NightViewItem: React.FC<Props> = (props) => {
  const {
    visit,
    hasPlaceColumn,
    currentRoom,
    isOnlineMeasurementsDisabled,
    openPatientTab,
    openCaseVisitTab,
    openMeasurementTab,
  } = props;
  const [isLoading, setLoading] = useState<boolean>(false);
  const i18next = useScopedTranslation('patients.list.options');
  const startMeasuring = useCallback(
    async (mode: MiniscreenMode) => {
      setLoading(true);
      await openWithMiniscreen({ patientId: visit.patientId, mode });
      setLoading(false);
    },
    [visit.patientId],
  );

  const isVisitForCurrentRoom = !currentRoom || currentRoom === visit.room?.id;
  const isMiniscreenOnlineModeEnabled =
    isMiniscreen() &&
    !isLoading &&
    (!isOnlineMeasurementsDisabled ||
      (isVisitForCurrentRoom && visit.endDate > new Date()));

  const isMiniscreenOfflineModeEnabled = isMiniscreen() && !isLoading;

  return (
    <tr
      key={visit.id}
      onClick={() => openCaseVisitTab(visit.patientId, visit.caseId, visit.id)}
      data-test-id="night-view-item"
      className={clsx(isVisitForCurrentRoom && styles.currentRoom)}
    >
      {hasPlaceColumn && <td className={styles.place}>{visit.room?.name}</td>}
      <td className={styles.timeRange}>
        {props.isStartDateHidden ? '-' : getFormattedTime(visit.startDate)}
      </td>
      <td
        className={styles.patientInfo}
        onClick={(e) => {
          e.stopPropagation();
          openPatientTab(visit.patientId);
        }}
      >
        <div className={styles.patientContainer}>
          <div className={styles.patient}>
            <div className={styles.patientName} title={visit.patientName}>
              {visit.patientName}
            </div>
          </div>
          <div className={styles.subtenantCode}>{visit.subtenantCode}</div>
        </div>
      </td>
      <td className={styles.visitType}>
        <div>
          <div>{visit.visitType}</div>
          <div>
            {visit.measurements.map((measurement) => {
              return (
                <UniLink
                  color={ButtonColor.Primary}
                  title={measurement.name}
                  className={styles['ms-link']}
                  onClick={(e) => {
                    e.stopPropagation();
                    openMeasurementTab(
                      visit.patientId,
                      visit.caseId,
                      visit.id,
                      measurement.id,
                    );
                  }}
                />
              );
            })}
          </div>
        </div>
      </td>
      {isMiniscreen() && (
        <td className={styles.controls} onClick={(e) => e.stopPropagation()}>
          {props.startMiniscreenOnline && (
            <IconButton
              disabled={!isMiniscreenOnlineModeEnabled}
              color="blue"
              onClick={() => startMeasuring(MiniscreenMode.Online)}
              className={styles.startButton}
              iconComponent={
                <MiniscreenModeIcon
                  type="online"
                  disabled={!isMiniscreenOnlineModeEnabled}
                  className={styles.icon}
                />
              }
              disabledTooltip={i18next.t('miniscreen_online_disabled')}
              tooltip={i18next.t('start_miniscreen_online')}
            />
          )}
          {props.startMiniscreenOffline && (
            <IconButton
              disabled={!isMiniscreenOfflineModeEnabled}
              color="blue"
              onClick={() => startMeasuring(MiniscreenMode.Offline)}
              className={styles.startButton}
              iconComponent={
                <MiniscreenModeIcon type="offline" className={styles.icon} />
              }
              tooltip={i18next.t('start_miniscreen_offline')}
            />
          )}
        </td>
      )}
    </tr>
  );
};
